import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Provider global que registra errores JS no controlados durante la sesión
// para que el modal de "Reportar incidencia" (Fase 4) pueda mostrarlos al
// usuario y dejarlo decidir si los incluye en el reporte. El buffer es en
// memoria — se limpia al recargar la página. Tamaño acotado a BUFFER_MAX
// para evitar uso desproporcionado de memoria si una página loopea errores.

const BUFFER_MAX = 10;

const ErrorCaptureContext = createContext(null);

const makeEntry = (type, partial) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type,
  timestamp: new Date().toISOString(),
  url: typeof window !== 'undefined' ? window.location.href : null,
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  ...partial,
});

export const ErrorCaptureProvider = ({ children }) => {
  const bufferRef = useRef([]);
  const [errors, setErrors] = useState([]);

  const pushEntry = useCallback((entry) => {
    const next = [...bufferRef.current, entry].slice(-BUFFER_MAX);
    bufferRef.current = next;
    setErrors(next);
  }, []);

  const clear = useCallback(() => {
    bufferRef.current = [];
    setErrors([]);
  }, []);

  useEffect(() => {
    const handleError = (event) => {
      pushEntry(
        makeEntry('error', {
          message: String(event?.message ?? event?.error?.message ?? 'Unknown error'),
          stack: event?.error?.stack ?? null,
          source: event?.filename ?? null,
          lineno: event?.lineno ?? null,
          colno: event?.colno ?? null,
        })
      );
    };

    const handleRejection = (event) => {
      const reason = event?.reason;
      const message =
        (reason && (reason.message || reason.toString?.())) ||
        String(reason ?? 'Unhandled promise rejection');
      pushEntry(
        makeEntry('unhandledrejection', {
          message,
          stack: reason?.stack ?? null,
        })
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [pushEntry]);

  const value = useMemo(() => ({ errors, clear, pushEntry }), [errors, clear, pushEntry]);

  return <ErrorCaptureContext.Provider value={value}>{children}</ErrorCaptureContext.Provider>;
};

export const useErrorBuffer = () => {
  const ctx = useContext(ErrorCaptureContext);
  if (!ctx) {
    throw new Error(
      'useErrorBuffer debe usarse dentro de <ErrorCaptureProvider> (montado en LayoutMain)'
    );
  }
  return ctx;
};

export default ErrorCaptureProvider;
